'use client'

import { Select as SelectPrimitive } from 'radix-ui'
import React, { useState } from 'react'
import { InputBase, InputLabel } from '../InputBase/InputBase'
import type { WithInputBaseProps } from '../InputBase/InputBase'
import { cn } from '@/utils'
import { Chevron } from '@/icons'

export type SelectProps = React.ComponentPropsWithRef<
  typeof SelectPrimitive.Root
> &
  Pick<
    React.ComponentPropsWithRef<typeof SelectPrimitive.Content>,
    'position'
  > & {
    label?: string
    className?: string
    'aria-label'?: string

    /**
     * @deprecated use state instead
     */
    error?: boolean
  } & WithInputBaseProps

const Select = ({
  value,
  onValueChange,
  label,
  children,
  position = 'popper',
  className,
  error,
  size = 'lg',
  variant,
  isRounded,
  state,
  helperText,
  startAdornment,
  endAdornment,
  ...props
}: SelectProps) => {
  const [hasValue, setHasValue] = useState(() => Boolean(value))
  const triggerId = React.useId()

  // Determine the state to use - error prop takes precedence over state prop
  const finalState = error ? 'danger' : state

  return (
    <div className="flex flex-col gap-2.5">
      <div className="relative">
        <SelectPrimitive.Root
          value={value}
          onValueChange={(newValue) => {
            setHasValue(Boolean(newValue))
            onValueChange?.(newValue)
          }}
          {...props}
        >
          <SelectPrimitive.Trigger
            id={triggerId}
            aria-label={label || props['aria-label']}
            asChild
          >
            <InputBase
              size={size}
              variant={variant}
              isRounded={isRounded}
              state={finalState}
              helperText={helperText}
              className={cn(className, 'group bg-none')}
              inputContainerClassName="group-data-[state=open]:shadow-md"
              disabled={props.disabled}
              endAdornment={
                <SelectPrimitive.Icon asChild>
                  {endAdornment || (
                    <Chevron className="data-disabled:text-gray-400 absolute right-0 top-1/2 size-3 -translate-y-1/2 text-black transition-transform duration-200 group-data-[state=closed]:rotate-180" />
                  )}
                </SelectPrimitive.Icon>
              }
              startAdornment={startAdornment}
            >
              <button
                className="flex h-full w-full items-center p-4 pt-8 outline-none"
                type="button"
              >
                <div
                  className={cn({
                    'pl-8': !!startAdornment,
                  })}
                >
                  <SelectPrimitive.Value
                    className={cn('m-4 text-sm lg:text-xs')}
                  />
                </div>

                {label && (
                  <InputLabel
                    state={finalState}
                    isFloating={hasValue}
                    htmlFor={triggerId}
                  >
                    {label}
                  </InputLabel>
                )}
              </button>
            </InputBase>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className={cn(
                'max-h-(--radix-select-content-available-height) origin-(--radix-select-content-transform-origin) data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 relative z-50 min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border border-gray-200 bg-white text-gray-600 shadow-md',
                {
                  'data-[side=bottom]:translate-y-4 data-[side=left]:-translate-x-4 data-[side=right]:translate-x-4 data-[side=top]:-translate-y-4':
                    position === 'popper',
                  'data-[side=bottom]:!-translate-y-4': !!helperText,
                },
              )}
              position={position}
            >
              <SelectPrimitive.ScrollUpButton
                className={cn(
                  'flex cursor-default items-center justify-center py-1',
                )}
              >
                <Chevron className="size-4" />
              </SelectPrimitive.ScrollUpButton>
              <SelectPrimitive.Viewport
                className={cn(
                  position === 'popper' &&
                    'min-w-(--radix-select-trigger-width) w-full',
                )}
              >
                {children}
              </SelectPrimitive.Viewport>
              <SelectPrimitive.ScrollDownButton
                className={cn(
                  'flex cursor-default items-center justify-center py-1',
                )}
              >
                <Chevron className="size-4 rotate-180" />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>
    </div>
  )
}

const SelectGroup = SelectPrimitive.Group

const SelectLabel = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    {...props}
  />
)
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    className={cn(
      'p-3xs relative flex w-full cursor-default select-none items-center rounded-sm text-sm text-gray-600 outline-none focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      'border-b border-gray-200 last:border-b-0',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)
SelectItem.displayName = SelectPrimitive.Item.displayName

export { Select, SelectGroup, SelectLabel, SelectItem }
